/**
 * YUMIX — Locality-aware welcome banner.
 *
 * Shown ONCE per user (sessionStorage + per-user-id flag) on the post-signup
 * screen. Fetches /api/locality/welcome?navigator_lang=... and renders a
 * culturally-aware greeting + 5 local cuisine examples that the Coach YUMIX
 * will use as personalized seed suggestions.
 *
 * Falls back silently if the endpoint fails — purely a delight feature.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, X } from 'lucide-react';
import { api } from '../context/AppContext';
import CoachYumixAvatar from './CoachYumixAvatar';

const FLAG_PREFIX = 'yumix_locality_welcome_seen_v1';

const LocalityWelcomeBanner = ({ userId }) => {
  const [bundle, setBundle] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let flagKey;
    try {
      flagKey = `${FLAG_PREFIX}_${userId}`;
      if (localStorage.getItem(flagKey)) {
        setDismissed(true);
        return;
      }
    } catch (_) {
      // localStorage unavailable (private mode, quota, etc.) — show banner anyway
    }
    const navigatorLang =
      (typeof navigator !== 'undefined' && navigator.language) || '';
    let alive = true;
    (async () => {
      try {
        const res = await api.get(
          `/locality/welcome?navigator_lang=${encodeURIComponent(navigatorLang)}`
        );
        if (alive && res?.data) setBundle(res.data);
      } catch (_) {
        /* silent — delight feature; never block */
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId]);

  const close = () => {
    try {
      if (userId) {
        localStorage.setItem(`${FLAG_PREFIX}_${userId}`, '1');
      }
    } catch (_) {
      /* noop */
    }
    setDismissed(true);
  };

  if (dismissed || !bundle || !bundle.greeting || typeof bundle.greeting !== 'string') return null;
  // Only show banner if we matched a real region (not just the global fallback)
  if (bundle.matched === 'global_fallback') return null;
  // Defensive: cuisine_examples MUST be an array of strings, else hide chips silently.
  const cuisineExamples = Array.isArray(bundle.cuisine_examples)
    ? bundle.cuisine_examples.filter((d) => typeof d === 'string' && d).slice(0, 5)
    : [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="relative mb-4 p-3.5 rounded-xl bg-gradient-to-r from-primary/10 via-amber-500/10 to-pink-500/10 border border-primary/30"
        data-testid="locality-welcome-banner"
      >
        <button
          onClick={close}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full hover:bg-secondary/50 flex items-center justify-center"
          data-testid="locality-welcome-dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <CoachYumixAvatar variant="portrait" size="md" glowColor="orange" data-testid="locality-coach-avatar" />
          <div className="flex-1 min-w-0">
            <p className="font-heading text-base uppercase tracking-tight text-foreground" data-testid="locality-greeting">
              {bundle.greeting}
            </p>
            {bundle.city_hint && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                <span>{bundle.city_hint}{bundle.country_name ? ` · ${bundle.country_name}` : ''}</span>
              </p>
            )}
            {Array.isArray(cuisineExamples) && cuisineExamples.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="locality-cuisine-chips">
                {cuisineExamples.map((dish) => (
                  <span
                    key={dish}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary/60 border border-border text-foreground"
                  >
                    {dish}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LocalityWelcomeBanner;
